import { useNavigate } from "react-router-dom";
import { useSuspenseQuery } from "@tanstack/react-query";
import { getGrowthCoachDashboard } from "../../services/adminService";
import { queryKeys } from "../../utilities/queryKeys";
import { DataTable } from "../../Components/ui/data-table";
import { type ColumnDef } from "@tanstack/react-table";
import { format, parseISO } from "date-fns";
import { Card, CardContent } from "../../Components/ui/card";
import { Badge } from "../../Components/ui/badge";
import DashboardPageLayout from "../../Components/DashboardPageLayout";
import {
  AlertTriangle,
  Clock,
  ClipboardList,
  type LucideIcon,
} from "lucide-react";

interface GrowthCoachRow {
  id: string;
  companyName: string;
  date: Date;
  industry: string;
  status: string;
  gcName?: string;
  daysInReview: number;
}

const statusBadge = (status: string) => {
  if (status === "New Request") return <Badge variant="warning">{status}</Badge>;
  if (status === "Review in Progress") return <Badge variant="default">{status}</Badge>;
  return <Badge variant="outline">{status}</Badge>;
};

const columns: ColumnDef<GrowthCoachRow, unknown>[] = [
  {
    accessorKey: "companyName",
    header: "Startup",
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => statusBadge(row.original.status),
  },
  {
    accessorKey: "gcName",
    header: "Growth Coach",
    cell: ({ getValue }) => (getValue() as string | undefined) || "—",
  },
  {
    accessorKey: "date",
    header: "Application Date",
    cell: ({ getValue }) => {
      const val = getValue();
      if (!val) return "-";
      try { return format(parseISO(String(val)), "MMM do yyyy"); } catch { return String(val); }
    },
  },
  {
    accessorKey: "industry",
    header: "Industry",
  },
  {
    accessorKey: "daysInReview",
    header: "Days in Review",
  },
];

type SeverityTier = "urgent" | "attention" | "info";

interface StatConfig {
  label: string;
  value: number;
  tier: SeverityTier;
  icon: LucideIcon;
}

const tierStyles: Record<SeverityTier, { bg: string; iconColor: string }> = {
  urgent: { bg: "bg-cDenied", iconColor: "text-cError" },
  attention: { bg: "bg-cLightYellow", iconColor: "text-amber-600" },
  info: { bg: "bg-cLightGreen", iconColor: "text-cSuccess" },
};

function StatCard({ config }: { config: StatConfig }) {
  const isActive = config.value > 0;
  const style = tierStyles[config.tier];
  const Icon = config.icon;

  return (
    <Card className={`${isActive ? style.bg : ""}`}>
      <CardContent className="p-4 flex flex-col items-center gap-1.5">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${isActive ? style.iconColor : "text-muted-foreground"}`} />
          <span className="text-2xl font-bold">{config.value}</span>
        </div>
        <p className="text-xs text-muted-foreground text-center leading-tight">{config.label}</p>
      </CardContent>
    </Card>
  );
}

const GrowthCoachDashboard = () => {
  const navigate = useNavigate();

  const { data: founderDetailsData } = useSuspenseQuery({
    queryKey: queryKeys.dashboards.growthCoach(),
    queryFn: () => getGrowthCoachDashboard(),
  });

  const handleViewMemberDetails = (row: GrowthCoachRow) => {
    navigate(`/dashboard/growth-coach-assessment/${row.id}`);
  };

  const contentArray = founderDetailsData.foundersArray;
  const newRequests = contentArray.filter((r: GrowthCoachRow) => r.status === "New Request");
  const inProgress = contentArray.filter((r: GrowthCoachRow) => r.status === "Review in Progress");

  const stats: StatConfig[] = [
    { label: "New Requests", value: newRequests.length, tier: "urgent", icon: AlertTriangle },
    { label: "In Progress", value: inProgress.length, tier: "attention", icon: Clock },
    { label: "Total Assigned", value: contentArray.length, tier: "info", icon: ClipboardList },
  ];

  return (
    <DashboardPageLayout>
      <h2>Welcome Back, {founderDetailsData.name}</h2>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 w-full">
        {stats.map(stat => (
          <StatCard key={stat.label} config={stat} />
        ))}
      </div>

      <div className="w-full">
        <h3 className="text-lg font-semibold mb-3">Assessments</h3>
        {contentArray.length > 0 ? (
          <DataTable
            columns={columns}
            data={contentArray}
            onRowClick={handleViewMemberDetails}
            enableSorting
            enablePagination
          />
        ) : (
          <Card className="border-dashed">
            <CardContent className="p-8 flex flex-col items-center gap-3 text-center">
              <ClipboardList className="h-10 w-10 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">No assessments yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  New startup assessment requests will appear here when companies are assigned to your region.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardPageLayout>
  );
};

export default GrowthCoachDashboard;
